import { useEffect, useState } from "react";
import {
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Edit,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  QrCodeScanner,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import { OverviewFilterField } from "../../components/OverviewFilterField";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr } from "../../components/Functions";
import { getFetcher } from "../../components/Fetchers";
import { PayoutDTO } from "../../model/entity/PayoutDTO";
import { Loading } from "../../components/Loading";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/cs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PayoutDTODialog from "../../components/PayoutDTOEditDialog";
import QRCodeDialog from "../../components/QRCodeDialog";

const MonthSummary = () => {
  const [payouts, setPayouts] = useState<PayoutDTO[] | null>(null);
  const [filter, setFilter] = useState("");
  const [onlyNotPaid, setOnlyNotPaid] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [selectedPayoutDTO, setSelectedPayoutDTO] = useState<PayoutDTO | null>(
    null
  );
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openPayoutDialog, setOpenPayoutDialog] = useState(false);
  const [openQRCodeDialog, setOpenQRCodeDialog] = useState(false);

  useEffect(() => {
    if (!selectedDate || openPayoutDialog || openQRCodeDialog) return;

    const fetchData = async () => {
      const config = {
        year: selectedDate.year(),
        month: selectedDate.month() + 1,
      };
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/payouts/summary`,
          config
        );
        setPayouts(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData();
  }, [selectedDate, openPayoutDialog, openQRCodeDialog]);

  const handleShiftMonth = (val: number) => {
    if (!selectedDate) return;
    const newSelectedDate = selectedDate.add(val, "month");
    setSelectedDate(newSelectedDate);
  };

  const handleEditPayout = (p: PayoutDTO) => {
    setSelectedPayoutDTO(p);
    setOpenPayoutDialog(true);
  };

  const handlePayoutDTODialogCancel = () => {
    setOpenPayoutDialog(false);
    setTimeout(() => {
      setSelectedPayoutDTO(null);
    }, 100);
  };

  const handleQRCodeDialogCancel = () => {
    setOpenQRCodeDialog(false);
    setTimeout(() => {
      setSelectedPayoutDTO(null);
    }, 100);
  };

  const handleQRCode = (p: PayoutDTO) => {
    setSelectedPayoutDTO(p);
    setOpenQRCodeDialog(true);
  };

  if (!payouts) return <Loading />;

  const filteredPayouts = payouts.filter((p) => {
    const fullName = `${p.coach.firstName} ${p.coach.lastName}`.toLowerCase();
    return (!onlyNotPaid || !p.paid) && fullName.includes(filter.toLowerCase());
  });

  return (
    <div className="tableContainer">
      <div className="aboveTableWrapper">
        {selectedPayoutDTO && (
          <QRCodeDialog
            open={openQRCodeDialog}
            payoutDTO={selectedPayoutDTO}
            handleCancel={handleQRCodeDialogCancel}
          />
        )}
        {selectedPayoutDTO && (
          <PayoutDTODialog
            open={openPayoutDialog}
            selectedPayoutDTO={selectedPayoutDTO}
            handleCancel={handlePayoutDTODialogCancel}
          />
        )}
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={onlyNotPaid}
                onChange={() => setOnlyNotPaid(!onlyNotPaid)}
              />
            }
            label="Pouze nevyplacení"
            sx={{ mt: "1rem" }}
          />
        </div>
        <Paper
          elevation={0}
          sx={{
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            border: "1px solid var(--gradientEnd)",
            borderRadius: "2rem",
          }}
        >
          <IconButton
            sx={{ color: "var(--gradientEnd)" }}
            onClick={() => handleShiftMonth(-1)}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <Typography
            id={"dateLabel"}
            variant="h5"
            sx={{
              cursor: "pointer",
              color: "var(--gradientEnd)",
              mx: ".5rem",
              fontSize: "1.2rem",
            }}
            onClick={() => setOpenDatePicker(true)}
          >
            {dayjs(selectedDate)
              .locale("cs")
              .format("MMMM YYYY")
              .charAt(0)
              .toUpperCase() +
              dayjs(selectedDate).locale("cs").format("MMMM YYYY").slice(1)}
          </Typography>
          <IconButton
            sx={{ color: "var(--gradientEnd)" }}
            onClick={() => handleShiftMonth(1)}
          >
            <KeyboardArrowRight />
          </IconButton>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              open={openDatePicker}
              openTo="month"
              views={["year", "month"]}
              value={selectedDate}
              onClose={() => setOpenDatePicker(false)}
              onChange={(newValue) => setSelectedDate(newValue)}
              slotProps={{
                textField: {
                  sx: {
                    width: 0,
                    height: 0,
                    opacity: 0,
                    overflow: "hidden",
                    position: "absolute",
                  },
                  inputRef: (input) => {
                    if (input && openDatePicker) {
                      input.focus();
                    }
                  },
                },
                popper: {
                  anchorEl: document.getElementById("dateLabel"),
                  placement: "bottom",
                },
              }}
            />
          </LocalizationProvider>
        </Paper>
        <div>
          <OverviewFilterField setFilter={setFilter} />
        </div>
      </div>
      <Paper
        elevation={filteredPayouts.length ? 1 : 0}
        className="tableContainer"
        sx={{
          width: "70%",
          maxHeight: "48rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
          <Table size="small">
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 2,
              }}
            >
              <TableRow className="tableHeader">
                <TableCell className="tableHeaderCell tableHeaderFirstCell">
                  Trenér
                </TableCell>
                <TableCell className="tableHeaderCell">Tréninky</TableCell>
                <TableCell className="tableHeaderCell">Hodiny</TableCell>
                <TableCell className="tableHeaderCell">Částka (Kč)</TableCell>
                <TableCell className="tableHeaderCell">Vyplaceno</TableCell>
                <TableCell className="tableHeaderCell">QR Kód</TableCell>
                <TableCell className="tableHeaderCell tableHeaderLastCell">
                  Akce
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayouts.map((p) => (
                <TableRow key={p.id} className="tableRow">
                  <TableCell className="tableCell tableFirstCell">
                    {p.coach.firstName + " " + p.coach.lastName}
                  </TableCell>
                  <TableCell className="tableCell">{p.sessionsCnt}</TableCell>
                  <TableCell className="tableCell">{p.hoursCnt}</TableCell>
                  <TableCell className="tableCell">{p.amount}</TableCell>
                  <TableCell className="tableCell">
                    {p.paid ? (
                      <ThumbUp className="thumbUp" />
                    ) : (
                      <ThumbDown className="thumbDown" />
                    )}
                  </TableCell>
                  <TableCell className="tableCell">
                    <IconButton
                      size="small"
                      sx={{ color: "var(--gradientStart)" }}
                      onClick={() => handleQRCode(p)}
                    >
                      <QrCodeScanner />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    className="tableCell tableLastCell"
                    onClick={() => handleEditPayout(p)}
                  >
                    <Edit className="editBtn" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default MonthSummary;
